import { BadgeVariants } from "~/components/Badge";
import { Tooltip } from "~/components/Tooltip";
import { useWindowSize } from "~/utils/useWindowSize";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { cn } from "~/lib/utils";
import {
    DISTRIBUTION_TYPE_TOOLTIPS,
    RWA_URLS,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import { getBadgeClassName } from "~/components/Badge/component";
import { Anchor } from "~/components/Anchor";
import { PlatformUserEvents } from "~/modules/analytics/events/platforms";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addUtmTrackingToUrl } from "~/utils/addUtmTrackingToUrl";
import { DistributionTypes, MeasureValue } from "~/types";
import { uniqueValues } from "~/utils/functions/uniqueValues";

// // // //

/**
 * getUniqueTokenizationTypeIDs
 * Returns an array of unique tokenization type IDs across the given tokens.
 * NOTE - when any token has supply, this excludes tokens where total supply is 0
 * @param tokens
 * @returns Array of unique tokenization type IDs
 */
export function getUniqueTokenizationTypeIDs<
    T extends {
        total_supply_token: MeasureValue | null;
        tokenization_type_id: number | null;
    },
>(tokens: T[]) {
    // Set a flag if any of the tokens have a total_supply_token value greater than 0
    const hasSupply = tokens.some((t) => (t.total_supply_token?.val ?? 0) > 0);

    // Do any of the tokens have a supply? -> return the unique tokenization type IDs that have a supply
    if (hasSupply) {
        return uniqueValues(
            tokens
                .filter((t) => (t.total_supply_token?.val ?? 0) > 0)
                .map((t) => t.tokenization_type_id)
                .filter(Boolean)
                .sort(),
        );
    }

    // Do none of the tokens have a supply? -> return the unique tokenization type IDs without filtering by supply
    return uniqueValues(
        tokens.map((t) => t.tokenization_type_id).filter(Boolean),
    ).sort();
}

// // // //

export function TokenizationTypeBadge(props: {
    tokenization_type_id: number | null;
    size?: "sm" | "md" | "lg";
    className?: string;
    prefix?: string;
    suffix?: string;
}) {
    const {
        tokenization_type_id,
        size = "md",
        className,
        prefix = "",
        suffix = "",
    } = props;

    // Use window size to determine placement + width of tooltip
    const { isSm } = useWindowSize();

    // Return null if no tokenization type is available
    if (!tokenization_type_id) {
        return null;
    }

    // Get the "Distribution Type" from the tokenization type
    const distributionType =
        tokenization_type_id === TOKENIZATION_TYPE_IDS.OnChainRepresented
            ? DistributionTypes.Represented
            : DistributionTypes.Distributed;

    // Map distribution type to label
    const label =
        distributionType === DistributionTypes.Distributed
            ? "Distributed"
            : "Represented";

    // Map distribution type to badge variant
    const badgeVariant =
        distributionType === DistributionTypes.Distributed
            ? BadgeVariants.blue
            : BadgeVariants.orange;

    // Add prefix if provided
    const labelWithPrefix = prefix ? `${prefix} ${label}` : label;
    const labelWithSuffix = suffix
        ? `${labelWithPrefix} ${suffix}`
        : labelWithPrefix;

    // Map tokenization type to description
    const description = DISTRIBUTION_TYPE_TOOLTIPS[distributionType];

    // Construct <a> styled to look like a badge component
    const badge = (
        <Anchor
            openInNewTab
            href={addUtmTrackingToUrl(RWA_URLS.taxonomyDocs)}
            analyticsLabel={PlatformUserEvents.ClickIndividualTaxonomyBadge}
            className={getBadgeClassName({
                variant: badgeVariant,
                size,
                className: cn("px-3 py-0.5 cursor-pointer", className),
                hover: true,
                rounded: "md",
            })}
        >
            {labelWithSuffix}
            <FontAwesomeIcon
                icon={faExternalLink}
                className={cn("ml-1.5", {
                    "h-2.5": size === "sm",
                    "h-3": size === "md",
                })}
            />
        </Anchor>
    );

    // Return badge wrapped in tooltip with description
    return (
        <Tooltip
            tooltipContent={
                <div
                    className={cn("p-3 text-left", {
                        "w-64": isSm,
                        "w-96": !isSm,
                    })}
                >
                    <p className="font-light text-sm">{label} Asset</p>
                    <p className="text-xs font-light mt-1">{description}</p>
                </div>
            }
            placement={isSm ? "bottom" : "right"}
        >
            {({ setTriggerRef }) => <span ref={setTriggerRef}>{badge}</span>}
        </Tooltip>
    );
}

export function MultiTokenizationTypeBadge(props: {
    size?: "sm" | "md" | "lg";
    className?: string;
}) {
    const { size = "md", className } = props;

    // Use window size to determine placement + width of tooltip
    const { isSm } = useWindowSize();

    // Map distribution type to label
    const label = "Hybrid";

    // Construct <a> styled to look like a badge component
    const badge = (
        <Anchor
            openInNewTab
            href={addUtmTrackingToUrl(RWA_URLS.taxonomyDocs)}
            analyticsLabel={PlatformUserEvents.ClickIndividualTaxonomyBadge}
            className={getBadgeClassName({
                variant: BadgeVariants.gray,
                size,
                className: cn("px-3 py-0.5 cursor-pointer", className),
                hover: true,
                rounded: "md",
            })}
        >
            {label}
            <FontAwesomeIcon
                icon={faExternalLink}
                className={cn("ml-1.5", {
                    "h-2.5": size === "sm",
                    "h-3": size === "md",
                })}
            />
        </Anchor>
    );

    // Return badge wrapped in tooltip with description
    return (
        <Tooltip
            tooltipContent={
                <div
                    className={cn("p-3 text-left", {
                        "w-64": isSm,
                        "w-96": !isSm,
                    })}
                >
                    <p className="font-light text-sm">{label} Asset</p>
                    <p className="text-xs font-light mt-1">
                        Hybrid tokenization type that includes both distributed
                        and represented tokens.
                    </p>
                </div>
            }
            placement={isSm ? "bottom" : "right"}
        >
            {({ setTriggerRef }) => <span ref={setTriggerRef}>{badge}</span>}
        </Tooltip>
    );
}
